import * as z from 'zod';

import { RateSource } from '@/modules/common/types/rate-source';
import { RateMask } from '@/modules/common/utils/rate-mask';

import { MAX_RATE_NUMBER, MIN_RATE_NUMBER } from '../constants';
import {
  ASSIGNMENT_PROBABILITY_MAX,
  ASSIGNMENT_PROBABILITY_MIN,
  isAssignmentProbabilityVisible,
} from '../helpers/assignment/probability';
import { AssignmentStatus } from '../types/assignment-status';

export const assignmentFormSchema = z
  .object({
    userId: z.string().trim().nonempty('authenticated.planner:form.userId.required'),
    projectId: z.string().trim().nonempty('authenticated.planner:form.projectId.required'),
    scheduledWork: z.string().trim().nonempty('authenticated.planner:form.scheduledWork.required'),
    dateRange: z.object({
      from: z.date({
        required_error: 'authenticated.planner:form.dateRange.from.required',
        invalid_type_error: 'authenticated.planner:form.dateRange.from.invalidType',
      }),
      to: z.date({
        required_error: 'authenticated.planner:form.dateRange.to.required',
        invalid_type_error: 'authenticated.planner:form.dateRange.to.invalidType',
      }),
    }),
    status: z.nativeEnum(AssignmentStatus),
    probability: z.number().nullable(),
    rateSource: z.nativeEnum(RateSource).optional(),
    rate: z.string(),
    currency: z.string(),
    isBillable: z.boolean(),
    doSkipWeekdays: z.boolean(),
    isBackgroundWork: z.boolean(),
    note: z.string().trim().optional(),
  })
  .refine(data => data.rateSource, {
    message: 'authenticated.planner:form.rateSource.required',
    path: ['rateSource'], // Attach the error to endDate field
  })
  .refine(data => data.dateRange.from <= data.dateRange.to, {
    message: 'authenticated.planner:form.dateRange.to.beforeStart',
    path: ['dateRange.to'], // Attach the error to endDate field
  })
  .superRefine((data, ctx) => {
    if (!isAssignmentProbabilityVisible(data.status)) return;

    if (data.probability === null || data.probability === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'authenticated.planner:form.probability.required',
        path: ['probability'],
      });
      return;
    }

    if (!Number.isInteger(data.probability)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'authenticated.planner:form.probability.integer',
        path: ['probability'],
      });
      return;
    }

    if (data.probability < ASSIGNMENT_PROBABILITY_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'authenticated.planner:form.probability.min',
        path: ['probability'],
      });
    }

    if (data.probability > ASSIGNMENT_PROBABILITY_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'authenticated.planner:form.probability.max',
        path: ['probability'],
      });
    }
  })
  .refine(
    data => {
      // If rate source is not custom, validation passes
      if (data.rateSource !== RateSource.Custom) return true;

      // A masked rate is the backend's stand-in for a value this user may not see
      // (PlannerFinancialsView). It is a legitimate untouched value: buildCommonPayload omits
      // customRate when it is masked, so the backend keeps the stored rate. This lets someone
      // without financial access edit an assignment's dates without having to overwrite a rate
      // they cannot read. Unreachable in create mode — nothing writes the sentinel there.
      if (RateMask.isMaskedStringValue(data.rate)) return true;

      // if empty string the rate isn't defined
      if (data.rate === '') return false;

      const rate = Number(data.rate);
      // If rate source is custom, rate must be a positive number (> 0)
      return rate >= MIN_RATE_NUMBER && rate <= MAX_RATE_NUMBER;
    },
    {
      message: 'authenticated.planner:form.rate.required',
      path: ['rate'],
    }
  )
  .refine(
    data => {
      // If rate source is not custom, currency validation passes (it's set from API)
      if (data.rateSource !== RateSource.Custom) return true;

      // If rate source is custom, currency must be selected
      return data.currency.trim().length > 0;
    },
    {
      message: 'authenticated.planner:form.currency.required',
      path: ['currency'],
    }
  );
